import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { RefreshCw, Users, PoundSterling, Mail } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { useAccount } from "@/context/AccountContext";
import { getAdminOverview, resyncSendy } from "@/lib/api";
import { CardSkeleton, SkeletonBlock } from "@/components/Skeletons";

const TIER_CLS = {
  free: "bg-zinc-500/10 text-zinc-300 border-zinc-500/25",
  plus: "bg-sky-500/10 text-sky-300 border-sky-500/25",
  pro: "bg-emerald-500/10 text-emerald-300 border-emerald-500/25",
};

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}
function money(n, cur = "gbp") {
  const sym = { gbp: "£", usd: "$", eur: "€" }[cur] || `${cur.toUpperCase()} `;
  return `${sym}${Number(n || 0).toFixed(2)}`;
}

export default function Admin() {
  const { account, isAdmin } = useAccount();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try { setData(await getAdminOverview()); setErr(null); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  if (account && !isAdmin) return <Navigate to="/dashboard" replace />;

  const mrr = data?.mrr || {};
  const mrrTotal = mrr.monthly ? Object.entries(mrr.monthly).map(([c, v]) => money(v, c)).join(" + ") : (mrr.source === "unconfigured" ? "Stripe not configured" : "—");

  return (
    <div className="max-w-7xl mx-auto space-y-8" data-testid="admin-page">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-1">Internal</div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">Subscribers</h1>
          <p className="text-sm text-zinc-400 mt-2">Sign-ups, tier breakdown and rough MRR from Stripe {data?.billing_mode === "live" ? "live" : "test"} data.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={async () => { try { const r = await resyncSendy(); toast.success("Sendy resync queued", { description: `${r.queued} contacts` }); } catch (e) { toast.error("Resync failed", { description: e.message }); } }} className="inline-flex items-center gap-2 rounded-lg border border-[#272A30] px-3 py-2 text-xs uppercase tracking-wider text-zinc-400 hover:text-white hover:bg-[#14171D]" data-testid="admin-resync-sendy">
            <Mail className="h-3.5 w-3.5" /> Resync Sendy
          </button>
          <button onClick={load} className="inline-flex items-center gap-2 rounded-lg border border-[#272A30] px-3 py-2 text-xs uppercase tracking-wider text-zinc-400 hover:text-white hover:bg-[#14171D]" data-testid="admin-refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </header>

      {err && <div role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm px-4 py-3">{err}</div>}

      <section className="grid grid-cols-1 sm:grid-cols-4 gap-4" data-testid="admin-stats">
        {!data ? [1, 2, 3, 4].map((i) => <CardSkeleton key={i} />) : (
          <>
            <Stat icon={Users} label="Sign-ups" value={data.totals.signups} hint={`${data.totals.checks_today} checks today`} />
            <Stat label="Free" value={data.tiers.free} hint="No subscription" />
            <Stat label="Plus / Pro" value={`${data.tiers.plus} / ${data.tiers.pro}`} hint={`${mrr.subscriptions?.active ?? 0} active · ${mrr.subscriptions?.past_due ?? 0} past due`} />
            <Stat icon={PoundSterling} label="Rough MRR" value={mrrTotal} hint={mrr.by_tier ? Object.entries(mrr.by_tier).map(([t, v]) => `${t} ${money(v)}`).join(" · ") : (mrr.error || "")} accent="text-emerald-400" />
          </>
        )}
      </section>

      {data?.sendy && (
        <section className="rounded-xl border border-[#272A30] bg-[#111318] p-5" data-testid="admin-sendy">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold tracking-tight">Sendy lists</h2>
            <span className="text-xs text-zinc-500">{data.sendy.configured ? "mail.xnet.tools" : "Not configured"}</span>
          </div>
          <div className="mt-3 grid sm:grid-cols-3 gap-3 text-sm">
            {["free", "plus", "pro"].map((t) => {
              const l = data.sendy.lists?.[t];
              return (
                <div key={t} className="rounded-lg border border-[#272A30] px-4 py-3 flex items-center justify-between">
                  <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] uppercase tracking-wider ${TIER_CLS[t]}`}>{t}</span>
                  <span className="mono text-zinc-300">{l ? (l.error ? "error" : `${l.active ?? "?"} active`) : "—"}</span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="rounded-xl border border-[#272A30] bg-[#111318]" data-testid="admin-signups">
        <div className="px-5 py-4 border-b border-[#272A30]">
          <h2 className="text-base font-semibold tracking-tight">Sign-up list</h2>
          <p className="text-xs text-zinc-500 mt-0.5">Newest first. Tier is the entitled tier after Stripe status is applied.</p>
        </div>
        {!data ? (
          <div className="p-5 space-y-3">{[1, 2, 3].map((i) => <SkeletonBlock key={i} className="h-10 w-full" />)}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                  <th className="px-5 py-3 text-left font-medium">Email</th>
                  <th className="px-5 py-3 text-left font-medium">Tier</th>
                  <th className="px-5 py-3 text-left font-medium">Signed up</th>
                  <th className="px-5 py-3 text-left font-medium">Today</th>
                  <th className="px-5 py-3 text-left font-medium">Stripe</th>
                  <th className="px-5 py-3 text-left font-medium">Sendy</th>
                </tr>
              </thead>
              <tbody>
                {data.signups.map((s) => (
                  <tr key={s.user_id} className="border-t border-[#272A30]/60" data-testid={`signup-row-${s.user_id}`}>
                    <td className="px-5 py-3 text-zinc-200">{s.email}</td>
                    <td className="px-5 py-3"><span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] uppercase tracking-wider ${TIER_CLS[s.tier]}`}>{s.tier}</span></td>
                    <td className="px-5 py-3 text-zinc-400 whitespace-nowrap">{fmt(s.signup_date)}</td>
                    <td className="px-5 py-3 text-zinc-400 tabular-nums">{s.checks_today}</td>
                    <td className="px-5 py-3 text-zinc-400 mono text-xs">{s.subscription_status || (s.stripe_customer_id ? "customer" : "—")}</td>
                    <td className="px-5 py-3 text-xs">
                      {s.sendy_last_error ? <span className="text-red-400" title={s.sendy_last_error}>error</span> : s.sendy_synced_at ? <span className="text-emerald-400">{fmt(s.sendy_synced_at)}</span> : <span className="text-zinc-500">pending</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({ icon: Icon, label, value, hint, accent }) {
  return (
    <div className="rounded-xl border border-[#272A30] bg-[#111318] p-5">
      <div className="flex items-start justify-between">
        <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
        {Icon && <Icon className={`h-4 w-4 ${accent || "text-zinc-500"}`} />}
      </div>
      <div className={`mt-4 text-3xl font-semibold tracking-tight tabular-nums truncate ${accent || ""}`}>{value}</div>
      <div className="mt-1 text-xs text-zinc-500 truncate">{hint}</div>
    </div>
  );
}
